import { useRef } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Button } from "@transfr-inc/dashboard-components/forms";

import {
  SYLLABUS_ACCEPTED_MIME_TYPES,
  SYLLABUS_FILE_ACCEPT,
  SYLLABUS_MAX_FILE_BYTES,
  SYLLABUS_MAX_FILE_MB,
  SYLLABUS_UPLOAD_ERRORS,
} from "../ai-curriculum.constants";

import "./ai-syllabus-upload.step.scss";

const formatFileSize = (bytes) => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
};

// Client-side mirror of the backend upload validation (type + 100 MB cap) so
// obvious problems surface before any bytes are sent. Returns an error
// message, or null when the file is acceptable.
export const validateSyllabusFile = (file) => {
  if (!SYLLABUS_ACCEPTED_MIME_TYPES.has(file.type)) {
    return SYLLABUS_UPLOAD_ERRORS.UNSUPPORTED_TYPE;
  }
  if (file.size > SYLLABUS_MAX_FILE_BYTES) {
    return SYLLABUS_UPLOAD_ERRORS.TOO_LARGE;
  }
  return null;
};

/**
 * Upload Syllabus entry step (ENG-23217) — presentational. The modal owns the
 * selected file, upload progress, and the footer "Upload & Analyze" CTA (the
 * primary action lives in the footer like every other step's).
 *
 * `failure` is the analysis failureReason when the analyzer (or a strict
 * criteria submit) failed; the step then doubles as the recovery screen —
 * pick a replacement file here, or use the footer's "Continue with Quick
 * Start" conversion.
 *
 * The library FileInput isn't used because it doesn't forward `accept` to its
 * hidden input, and the AC requires a type-filtered picker.
 */
export const AiSyllabusUploadStep = ({
  file,
  error,
  uploading,
  progress,
  failure,
  onFilePicked,
}) => {
  const inputRef = useRef(null);

  const handleChange = (event) => {
    const picked = event.target.files?.[0];
    // Reset so picking the same file again still fires onChange.
    event.target.value = "";
    if (picked) onFilePicked(picked);
  };

  return (
    <div className="ai-syllabus-upload-step">
      <div className="ai-syllabus-upload-step__illustration">
        <img src="/images/ai-curriculum/syllabus.png" alt="" />
      </div>

      <header className="ai-syllabus-upload-step__header">
        <h2>Upload Syllabus</h2>
        <p>
          Upload a PDF, Word document, or image (max {SYLLABUS_MAX_FILE_MB} MB)
          and we&apos;ll extract learning objectives and standards
          automatically.
        </p>
      </header>

      {failure && (
        <div className="ai-syllabus-upload-step__failure" role="alert">
          <FontAwesomeIcon icon="fa-regular fa-triangle-exclamation" />
          <div className="ai-syllabus-upload-step__failure-body">
            <strong>We couldn&apos;t analyze your syllabus.</strong>
            <p>{failure}</p>
            <p>
              Upload a different file to try again, or continue with Quick Start
              to enter your details manually.
            </p>
          </div>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={SYLLABUS_FILE_ACCEPT}
        className="ai-syllabus-upload-step__input"
        onChange={handleChange}
      />

      {file ? (
        <div className="ai-syllabus-upload-step__file">
          <FontAwesomeIcon icon="fa-regular fa-file-lines" />
          <span className="ai-syllabus-upload-step__file-name">
            {file.name}
          </span>
          <span className="ai-syllabus-upload-step__file-size">
            {formatFileSize(file.size)}
          </span>
          <Button
            className="ai-syllabus-upload-step__button"
            size="small"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            Replace
          </Button>
        </div>
      ) : (
        <Button
          className="ai-syllabus-upload-step__button"
          icon={["fa-regular", "upload"]}
          onClick={() => inputRef.current?.click()}
        >
          Choose File
        </Button>
      )}

      {error && (
        <p className="ai-syllabus-upload-step__error" role="alert">
          {error}
        </p>
      )}

      {uploading && (
        <div
          className="ai-syllabus-upload-step__progress"
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="ai-syllabus-upload-step__progress-fill"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};
